import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../auth/AuthContext';
import { API_BASE, s, authHeaders, formatRelativeDate } from './sharedStyles';

const PLAN_LABELS = {
  SOLO: 'Solo',
  HIGH_STREET: 'High Street',
  CHAIN: 'Chain',
  INTERNAL: 'Internal',
};

const PROTECTED_TENANTS = new Set([
  '00000000-0000-0000-0000-000000000000', // Admin
  '00000000-0000-0000-0000-000000000001', // Demo
]);
const DEMO_SHOP_ID = '00000000-0000-0000-0000-000000000002';

// Pick the expiry that actually drives the tenant's access:
// paid subscribers use currentPeriodEnd, trialing tenants use trialEndsAt.
function activeExpiry(tn) {
  if (tn.hasStripeSubscription && tn.currentPeriodEnd) {
    return { kind: 'paid', at: tn.currentPeriodEnd };
  }
  if (tn.trialEndsAt) {
    return { kind: 'trial', at: tn.trialEndsAt };
  }
  return { kind: 'none', at: null };
}

function daysUntil(iso) {
  if (!iso) return null;
  const diffMs = new Date(iso).getTime() - Date.now();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

// yyyy-mm-dd formatter for <input type="date"> in the user's local time.
function toDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Convert yyyy-mm-dd to an ISO Instant at end-of-day (23:59:59) local,
// so the expiry covers the whole selected day regardless of timezone drift.
function fromDateInput(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 0).toISOString();
}

export default function TenantsPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [tenantShops, setTenantShops] = useState({});
  const [tenantUsers, setTenantUsers] = useState({});
  const [busyTenant, setBusyTenant] = useState(null);
  const [busyUser, setBusyUser] = useState(null);
  const [busyShop, setBusyShop] = useState(null);
  const [busyPlan, setBusyPlan] = useState(null);
  const [editingExpiry, setEditingExpiry] = useState(null); // tenantId being edited
  const [expiryDraft, setExpiryDraft] = useState('');
  const [busyExpiry, setBusyExpiry] = useState(null);

  const loadTenants = useCallback(() => {
    fetch(`${API_BASE}/api/v1/admin/tenants`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : [])
      .then(setTenants)
      .catch((e) => console.error('Tenants fetch failed', e));
  }, [token]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const toggleTenant = (tenantId) => {
    if (expandedTenant === tenantId) { setExpandedTenant(null); return; }
    setExpandedTenant(tenantId);
    if (!tenantShops[tenantId]) {
      fetch(`${API_BASE}/api/v1/admin/tenants/${tenantId}/shops`, { headers: authHeaders(token) })
        .then((r) => r.ok ? r.json() : [])
        .then((data) => setTenantShops((prev) => ({ ...prev, [tenantId]: data })))
        .catch((e) => console.error('Shops fetch failed', e));
    }
    if (!tenantUsers[tenantId]) {
      fetch(`${API_BASE}/api/v1/admin/tenants/${tenantId}/users`, { headers: authHeaders(token) })
        .then((r) => r.ok ? r.json() : [])
        .then((data) => setTenantUsers((prev) => ({ ...prev, [tenantId]: data })))
        .catch((e) => console.error('Users fetch failed', e));
    }
  };

  const toggleFreezeTenant = async (tenantId) => {
    setBusyTenant(tenantId);
    try {
      const res = await fetch(`${API_BASE}/api/v1/admin/tenants/${tenantId}/toggle-freeze`, {
        method: 'PATCH',
        headers: authHeaders(token),
      });
      if (res.ok) {
        const updated = await res.json();
        setTenants((prev) => prev.map((tn) => tn.id === tenantId ? { ...tn, frozen: updated.frozen } : tn));
      } else {
        console.error('Toggle freeze failed:', res.status, await res.text());
      }
    } catch (e) { console.error('Toggle freeze error', e); }
    finally { setBusyTenant(null); }
  };

  const toggleFreezeShop = async (tenantId, shopId) => {
    setBusyShop(shopId);
    try {
      const res = await fetch(`${API_BASE}/api/v1/admin/shops/${shopId}/toggle-active`, {
        method: 'PATCH',
        headers: authHeaders(token),
      });
      if (res.ok) {
        const updated = await res.json();
        setTenantShops((prev) => ({
          ...prev,
          [tenantId]: (prev[tenantId] || []).map((sh) => sh.id === shopId ? { ...sh, active: updated.active } : sh),
        }));
      } else {
        console.error('Toggle shop active failed:', res.status, await res.text());
      }
    } catch (e) { console.error('Toggle shop active error', e); }
    finally { setBusyShop(null); }
  };

  const setPlan = async (tenantId, plan) => {
    const tenant = tenants.find((tn) => tn.id === tenantId);
    if (!tenant || tenant.plan === plan) return;

    const oldLabel = PLAN_LABELS[tenant.plan] || tenant.plan;
    const newLabel = PLAN_LABELS[plan] || plan;
    const confirmed = window.confirm(
      `Change "${tenant.name}" plan from ${oldLabel} to ${newLabel}?\n\n` +
      `This will take effect immediately and may alter the tenant's quota and feature access.`
    );
    if (!confirmed) {
      // Force a re-render so the controlled <select> snaps back to tenant.plan.
      setTenants((prev) => [...prev]);
      return;
    }

    setBusyPlan(tenantId);
    try {
      const res = await fetch(`${API_BASE}/api/v1/admin/tenants/${tenantId}/plan`, {
        method: 'PATCH',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      });
      if (res.ok) {
        const updated = await res.json();
        setTenants((prev) => prev.map((tn) => tn.id === tenantId ? { ...tn, plan: updated.plan } : tn));
      } else {
        console.error('Set plan failed:', res.status, await res.text());
        setTenants((prev) => [...prev]);
      }
    } catch (e) {
      console.error('Set plan error', e);
      setTenants((prev) => [...prev]);
    }
    finally { setBusyPlan(null); }
  };

  const openExpiryEditor = (tn) => {
    const exp = activeExpiry(tn);
    setExpiryDraft(toDateInput(exp.at));
    setEditingExpiry(tn.id);
  };

  const saveExpiry = async (tn) => {
    const iso = fromDateInput(expiryDraft);
    if (!iso) { setEditingExpiry(null); return; }
    setBusyExpiry(tn.id);
    const body = tn.hasStripeSubscription
      ? { currentPeriodEnd: iso }
      : { trialEndsAt: iso };
    try {
      const res = await fetch(`${API_BASE}/api/v1/admin/tenants/${tn.id}/expiry`, {
        method: 'PATCH',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const updated = await res.json();
        setTenants((prev) => prev.map((row) => row.id === tn.id ? { ...row, ...updated } : row));
        setEditingExpiry(null);
      } else {
        console.error('Set expiry failed:', res.status, await res.text());
      }
    } catch (e) { console.error('Set expiry error', e); }
    finally { setBusyExpiry(null); }
  };

  const toggleFreezeUser = async (tenantId, userId) => {
    setBusyUser(userId);
    try {
      const res = await fetch(`${API_BASE}/api/v1/admin/users/${userId}/toggle-active`, {
        method: 'PATCH',
        headers: authHeaders(token),
      });
      if (res.ok) {
        const updated = await res.json();
        setTenantUsers((prev) => ({
          ...prev,
          [tenantId]: (prev[tenantId] || []).map((u) => u.id === userId ? { ...u, active: updated.active } : u),
        }));
      } else {
        console.error('Toggle user active failed:', res.status, await res.text());
      }
    } catch (e) { console.error('Toggle user active error', e); }
    finally { setBusyUser(null); }
  };

  return (
    <div>
      <main style={s.main}>
        <h1 style={s.pageTitle}>{t('monitor.sections.tenants')}</h1>
        {tenants.length === 0 && <div style={{ color: '#64748B' }}>{t('monitor.tenants.noTenants')}</div>}
        {tenants.map((tn) => (
          <div key={tn.id} style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => toggleTenant(tn.id)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <span style={{ fontWeight: 700, color: '#F8FAFC', fontSize: '1rem' }}>{tn.name}</span>
                <span style={{ color: '#64748B', fontSize: '0.8rem' }}>{tn.slug}</span>
                {tn.frozen && <span style={s.frozenBadge}>{t('monitor.tenants.frozen')}</span>}
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {PROTECTED_TENANTS.has(tn.id) ? (
                  <span style={s.statusBadge(true)}>{tn.plan}</span>
                ) : (
                  <select
                    value={tn.plan}
                    disabled={busyPlan === tn.id}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setPlan(tn.id, e.target.value)}
                    style={{
                      padding: '0.25rem 0.4rem', borderRadius: 4, border: '1.5px solid #334155',
                      fontSize: '0.75rem', background: '#0F172A', color: '#F8FAFC',
                      fontWeight: 700, cursor: busyPlan === tn.id ? 'wait' : 'pointer',
                    }}
                  >
                    <option value="SOLO">Solo</option>
                    <option value="HIGH_STREET">High Street</option>
                    <option value="CHAIN">Chain</option>
                    <option value="INTERNAL">Internal</option>
                  </select>
                )}
                {(() => {
                  const exp = activeExpiry(tn);
                  if (exp.kind === 'none') return null;
                  const d = daysUntil(exp.at);
                  const expired = d != null && d < 0;
                  const label = exp.kind === 'paid'
                    ? (expired ? `Paid · ended ${-d}d ago` : `Paid · ${d}d left`)
                    : (expired ? `Trial · expired ${-d}d ago` : `Trial · ${d}d left`);
                  return (
                    <span
                      style={{
                        padding: '2px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700,
                        background: expired ? 'rgba(239,68,68,0.15)' : exp.kind === 'paid' ? 'rgba(16,185,129,0.15)' : 'rgba(6,182,212,0.15)',
                        color: expired ? '#EF4444' : exp.kind === 'paid' ? '#10B981' : '#06B6D4',
                      }}
                    >
                      {label}
                    </span>
                  );
                })()}
                {!PROTECTED_TENANTS.has(tn.id) && (
                  editingExpiry === tn.id ? (
                    <span
                      style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="date"
                        value={expiryDraft}
                        onChange={(e) => setExpiryDraft(e.target.value)}
                        style={{
                          padding: '0.25rem 0.4rem', borderRadius: 4, border: '1.5px solid #334155',
                          fontSize: '0.75rem', background: '#0F172A', color: '#F8FAFC',
                        }}
                      />
                      <button
                        disabled={busyExpiry === tn.id}
                        onClick={() => saveExpiry(tn)}
                        style={{ ...s.freezeBtn(true), cursor: busyExpiry === tn.id ? 'wait' : 'pointer' }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingExpiry(null)}
                        style={s.freezeBtn(false)}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); openExpiryEditor(tn); }}
                      style={{
                        padding: '4px 10px', borderRadius: 6, border: '1px solid #334155',
                        background: 'transparent', color: '#94A3B8', fontSize: '0.72rem',
                        fontWeight: 600, cursor: 'pointer',
                      }}
                      title={activeExpiry(tn).at ? new Date(activeExpiry(tn).at).toLocaleDateString() : 'Set expiry'}
                    >
                      {activeExpiry(tn).at ? new Date(activeExpiry(tn).at).toLocaleDateString() : 'Set expiry'}
                    </button>
                  )
                )}
                {tn.createdAt && (
                  <span style={{ fontSize: '0.75rem', color: '#64748B' }}>
                    {t('monitor.tenants.joined', { date: formatRelativeDate(tn.createdAt) })}
                  </span>
                )}
                <span style={{ color: '#94A3B8', fontSize: '0.8rem' }}>
                  {t('monitor.tenants.shopCount', { count: tn.shopCount })} · {t('monitor.tenants.userCount', { count: tn.userCount })}
                </span>
                {!PROTECTED_TENANTS.has(tn.id) && (
                  <button
                    style={s.freezeBtn(tn.frozen)}
                    disabled={busyTenant === tn.id}
                    onClick={(e) => { e.stopPropagation(); toggleFreezeTenant(tn.id); }}
                  >
                    {tn.frozen ? t('monitor.tenants.unfreeze') : t('monitor.tenants.freeze')}
                  </button>
                )}
                <span style={{ color: '#64748B' }}>{expandedTenant === tn.id ? '\u25B2' : '\u25BC'}</span>
              </div>
            </div>
            {expandedTenant === tn.id && (
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #334155' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <div style={{ ...s.cardTitle, marginBottom: '0.5rem' }}>{t('monitor.tenants.shops')}</div>
                    {(tenantShops[tn.id] || []).length === 0 && <div style={{ color: '#64748B', fontSize: '0.85rem' }}>{t('monitor.tenants.noShops')}</div>}
                    {(tenantShops[tn.id] || []).map((sh) => (
                      <div key={sh.id} style={{ ...s.statusRow, padding: '0.35rem 0' }}>
                        <span style={s.statusLabel}>{sh.name}</span>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span style={s.statusBadge(sh.active)}>{sh.active ? t('monitor.tenants.active') : t('monitor.tenants.inactive')}</span>
                          {sh.id !== DEMO_SHOP_ID && (
                            <button
                              style={s.freezeBtn(!sh.active)}
                              disabled={busyShop === sh.id}
                              onClick={() => toggleFreezeShop(tn.id, sh.id)}
                            >
                              {sh.active ? t('monitor.tenants.freeze') : t('monitor.tenants.unfreeze')}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{ ...s.cardTitle, marginBottom: '0.5rem' }}>{t('monitor.tenants.users')}</div>
                    {(tenantUsers[tn.id] || []).length === 0 && <div style={{ color: '#64748B', fontSize: '0.85rem' }}>{t('monitor.tenants.noUsers')}</div>}
                    {(tenantUsers[tn.id] || []).map((u) => (
                      <div key={u.id} style={{ ...s.statusRow, padding: '0.35rem 0' }}>
                        <span style={s.statusLabel}>{u.email}</span>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span style={{ ...s.statusBadge(true), background: 'rgba(99,102,241,0.15)', color: '#7C3AED' }}>{u.role}</span>
                          <button
                            style={s.freezeBtn(!u.active)}
                            disabled={busyUser === u.id}
                            onClick={() => toggleFreezeUser(tn.id, u.id)}
                          >
                            {u.active ? t('monitor.tenants.freeze') : t('monitor.tenants.unfreeze')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </main>
    </div>
  );
}
